import { Button, Icon } from '@components/ui';
import { GeneralHelper } from '@helpers';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';

import { OverlayShell } from '../OverlayShell';
import { CellRenderer, CellValue } from '../types';

import styles from './CellFullView.module.scss';

interface CellFullViewProps {
  filename: string;
  columnId: string;
  rows: Record<string, CellValue>[];
  rowIndex: number;
  rowNumbers: number[];
  renderer: CellRenderer;
  onNavigate: (nextIndex: number) => void;
  onClose: () => void;
}

const toRawText = (value: CellValue): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export const CellFullView = ({
  filename,
  columnId,
  rows,
  rowIndex,
  rowNumbers,
  renderer,
  onNavigate,
  onClose,
}: CellFullViewProps) => {
  const [showRaw, setShowRaw] = useState(false);
  const [copied, setCopied] = useState(false);

  const value = rows[rowIndex]?.[columnId];
  const rawText = toRawText(value);
  const total = rows.length;

  const navigate = (direction: -1 | 1) => {
    const next = rowIndex + direction;
    if (next >= 0 && next < total) onNavigate(next);
  };

  const copy = async () => {
    const ok = await GeneralHelper.copyToClipboard(rawText);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    }
  };

  const { Full } = renderer;

  return (
    <OverlayShell
      onClose={onClose}
      onArrowKey={navigate}
      maxWidth={860}
    >
      <div className={styles.head}>
        <button
          type="button"
          className={styles.iconBtn}
          aria-label="Previous row"
          disabled={rowIndex === 0}
          onClick={() => navigate(-1)}
        >
          <ChevronLeft size={18} />
        </button>
        <button
          type="button"
          className={styles.iconBtn}
          aria-label="Next row"
          disabled={rowIndex === total - 1}
          onClick={() => navigate(1)}
        >
          <ChevronRight size={18} />
        </button>
        <div className={styles.headTitle}>
          <div className={styles.colName}>{columnId}</div>
          <div className={styles.meta}>
            Row {rowNumbers[rowIndex]} of {total} · <span className={styles.mono}>{filename}</span>
          </div>
        </div>
        <div className={styles.sp} />
        <Button
          view="secondary"
          size="small"
          type="button"
          onClick={copy}
        >
          {copied ? 'Copied' : 'Copy value'}
        </Button>
        <Button
          view="secondary"
          size="small"
          type="button"
          onClick={() => setShowRaw(v => !v)}
        >
          {showRaw ? 'Show formatted' : 'Show raw text'}
        </Button>
        <button
          type="button"
          className={styles.iconBtn}
          aria-label="Close"
          onClick={onClose}
        >
          <Icon name="close" />
        </button>
      </div>
      <div className={styles.body}>
        {showRaw ? <pre className={styles.raw}>{rawText}</pre> : <Full value={value} />}
      </div>
    </OverlayShell>
  );
};
